import { computed, ref } from 'vue'

import { useLocalStorage } from '@vueuse/core'
import { isAxiosError } from 'axios'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'

import { useError } from '@/composables/useError'
import { routeNames } from '@/router/routeNames'
import { AuthRepository } from '@/services/api/auth'
import { RoleRepository } from '@/services/api/roles'
import { UserRepository } from '@/services/api/users'
import type {
  I2FAVerificationResponse,
  IAuth,
  ITwoFactorMethod,
} from '@/types/AuthTypes'
import type { IRolePermission } from '@/types/RoleTypes'
import { type IUser, LocalStorageKeys } from '@/types/UserTypes'

export const useUserStore = defineStore('userStore', () => {
  const { handleError } = useError()
  const userData = ref<Nullable<IUser>>(null)
  const permissions = ref<IRolePermission[]>([])
  const router = useRouter()
  const auth = useLocalStorage<IAuth>(LocalStorageKeys.AUTH, {} as IAuth)
  // Keeping in local storage to avoid race conditions during token refresh
  const isTokenRefreshing = useLocalStorage<boolean>(
    LocalStorageKeys.AUTH_IN_PROGRESS,
    false
  )

  const refreshToken = computed<Nullable<string>>(() => {
    return auth.value?.refreshToken || null
  })

  const getAuth = computed<Nullable<IAuth>>(() => {
    return auth.value
  })

  const getTwoFactorMethods = computed<ITwoFactorMethod[]>(() => {
    return userData.value?.twoFactorMethods || []
  })

  const token = computed<string | null>(() => auth.value?.accessToken || null)
  const isLoggedIn = computed<boolean>(() => {
    if (!token.value) return false
    return token.value.length > 0
  })

  const setAuth = (authData: IAuth): void => {
    auth.value = authData
  }

  const setUser = (user: IUser): void => {
    userData.value = user
  }

  const handleLogin = async (
    response: I2FAVerificationResponse
  ): Promise<void> => {
    if (!response.auth) return
    setAuth(response.auth)
    await fetchCurrentUser()
  }

  const fetchCurrentUser = async (): Promise<void> => {
    if (!token.value) return
    try {
      const userResponse = await UserRepository.getCurrentUser()
      userData.value = userResponse.data

      const userRoleUuid = userData.value.roles[0]?.uuid
      if (userRoleUuid) {
        const response =
          await RoleRepository.getPermissionsByRoleId(userRoleUuid)
        permissions.value = response.data
      }
    } catch (error) {
      if (isAxiosError(error) && error.status === 401) {
        clearAuth()
        await router.push({ name: routeNames.Login })
        return
      }
      handleError(error)
    }
  }

  const handleLogout = async (): Promise<void> => {
    try {
      await AuthRepository.logout()
    } catch (e) {
      console.error('Error during logout: ', e)
    } finally {
      clearAuth()
      router.push({ name: routeNames.Login })
    }
  }

  const clearAuth = (): void => {
    userData.value = null
    permissions.value = []
    auth.value = null
    isTokenRefreshing.value = false
  }

  const refresh = async (): Promise<void> => {
    if (!refreshToken.value || isTokenRefreshing.value) return
    isTokenRefreshing.value = true
    try {
      const { data } = await AuthRepository.refreshAuth(refreshToken.value)
      setAuth(data)
    } catch (e) {
      handleError(e)
      await handleLogout()
    } finally {
      isTokenRefreshing.value = false
    }
  }

  const availablePermissions = computed((): string[] => {
    // TODO -> do usuniecia, tymczasowe roziazanie. W tej wersji API co mam permissions zwraca string[], w kolejnych to bedzie IRolePermission[]
    if (typeof permissions.value[0] == 'string')
      return permissions.value as unknown as string[]
    return permissions.value.map(p => p.label)
  })

  setInterval(async (): Promise<void> => {
    if (!auth.value) return
    if (!auth.value.accessTokenExpiresIn) return

    const expiryDate = new Date(auth.value.accessTokenExpiresIn)

    const timeLeft = (expiryDate.getTime() - new Date().getTime()) / 1000 // in seconds
    const timeToReauth = 60 * 3 // 3 minutes

    if (timeLeft < timeToReauth) {
      await refresh()
    }
  }, 1000 * 60) // 1 minute

  return {
    userData,
    auth,
    token,
    isLoggedIn,
    refreshToken,
    getAuth,
    getTwoFactorMethods,
    fetchCurrentUser,
    handleLogin,
    clearAuth,
    logout: handleLogout,
    refresh,
    setAuth,
    setUser,
    availablePermissions,
  }
})
